import React, { useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import styled from "styled-components";
import { FiCheckCircle, FiMail, FiPhone } from "react-icons/fi";
import { useLanguage } from "./LanguageContext";

/* ====== UI Theme ====== */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
};

const Page = styled.main`

  background: ${COLORS.subtle};
  padding: clamp(12px, 4vw, 24px);

  /* დიდ ეკრანებზე ცენტრი, მაგრამ სკროლიც იყოს საჭიროებისას */
  display: grid;
  justify-items: center;
  align-content: center;

  overflow: auto;
  -webkit-overflow-scrolling: touch;

  /* პატარა ეკრანებზე ზემოდან დავიწყოთ, რომ header არ „იჭრას“ */
  @media (max-width: 640px) {
    align-content: start;
    padding-top: max(12px, env(safe-area-inset-top));
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`;

const Card = styled.section`
  width: min(720px, 100%);
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,.08);
  padding: clamp(16px, 3.5vw, 28px);
  display: grid;
  gap: 12px;

  /* ბარათი არასდროს გაცდეს ეკრანს; შიგნით დაიშალოს scroll */
  max-height: 100%;
  overflow: auto;

  @media (max-width: 640px) {
    border-radius: 12px;
    gap: 10px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Icon = styled.div`
  color: #16a34a;
  font-size: clamp(24px, 6vw, 30px);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(18px, 2.6vw, 22px);
  font-weight: 900;
  color: ${COLORS.text};
`;

const Subtle = styled.p`
  margin: 4px 0 0;
  color: ${COLORS.dim};
  font-size: clamp(13px, 2.8vw, 14px);
`;

const InfoBox = styled.div`
  margin-top: 6px;
  border: 1px solid ${COLORS.line};
  background: ${COLORS.subtle};
  border-radius: 12px;
  padding: 12px;
  display: grid;
  gap: 8px;
  color: ${COLORS.text};
  font-size: 14px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  word-break: break-word;
  min-width: 0;

  svg { opacity: .9; flex: 0 0 auto; }
  strong { flex: 0 0 auto; }
  span { flex: 1 1 200px; min-width: 0; }
`;

const H2 = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: ${COLORS.text};
`;

const P = styled.p`
  margin: 0;
  color: ${COLORS.dim};
  line-height: 1.55;
  font-size: 14px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;

  a { flex: 1 1 180px; text-align: center; }

  @media (max-width: 420px) {
    a { flex: 1 1 100%; }
  }
`;

const Button = styled(Link)`
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  color: ${COLORS.text};
  font-weight: 800;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;

const Primary = styled(Button)`
  background: ${COLORS.brand};
  border-color: ${COLORS.brand};
  color: #fff;
`;

/* Helper: სცდის t(key)-ს, თუ არა — აბრუნებს fallback-ს KA/EN */
function useTT() {
  const { t, language } = useLanguage();
  return (key, ka, en) => {
    try {
      const v = t ? t(key) : undefined;
      if (v && v !== key) return v;
    } catch {}
    return language === "ka" ? ka : en;
  };
}

export default function RegistrationSuccess() {
  const { state } = useLocation() || {};
  const pageRef = useRef(null);
  const TT = useTT();

  // გახსნისას ყოველთვის წავიდეს ზედაში (body-საც და შიდა კონტეინერსაც ვანულებთ scroll-ს)
  useEffect(() => {
    const prev = "scrollRestoration" in window.history ? window.history.scrollRestoration : null;
    if (prev !== null) window.history.scrollRestoration = "manual";

    // ცოტა defer, რომ layout დასრულდეს
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (pageRef.current) pageRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => {
      cancelAnimationFrame(id);
      if (prev !== null) window.history.scrollRestoration = prev;
    };
  }, []);

  const _defaultSubtitleKa =
    "რეგისტრაცია დასრულებულია ✅ გთხოვთ, გადაამოწმოთ ელფოსტა (დასტურის ბმული გაიგზავნა).";
  const _defaultSubtitleEn =
    "Registration complete ✅ Please check your email (a confirmation link was sent).";

  const subtitle =
    state?.message ||
    TT("success.subtitle", _defaultSubtitleKa, _defaultSubtitleEn);

  return (
    <Page ref={pageRef}>
      <Card>
        <Header>
          <Icon><FiCheckCircle /></Icon>
          <div>
            <Title>
              {TT("success.title", "რეგისტრაცია დასრულებულია", "Registration complete")}
            </Title>
            <Subtle aria-live="polite">{subtitle}</Subtle>
          </div>
        </Header>

        <InfoBox>
          {state?.email && (
            <Row>
              <FiMail />
              <strong style={{minWidth: 90}}>
                {TT("success.email", "ელფოსტა:", "Email:")}
              </strong>
              <span>{state.email}</span>
            </Row>
          )}
          {state?.phone && (
            <Row>
              <FiPhone />
              <strong style={{minWidth: 90}}>
                {TT("success.phone", "ტელეფონი:", "Phone:")}
              </strong>
              <span>{state.phone}</span>
            </Row>
          )}
        </InfoBox>

        <div style={{display:"grid", gap: 6, marginTop: 6}}>
          <H2>{TT("success.nextSteps", "შემდეგი ნაბიჯები", "Next steps")}</H2>
          <P>
            {TT(
              "success.checkEmail",
              "დასტურის ბმული გამოგიგზავნეთ თქვენს ელფოსტაზე. თუ წერილი არ ჩანს, გადაამოწმე ",
              "We’ve sent a confirmation link to your email. If you don’t see it, please check your "
            )}
            <strong>Spam/Junk</strong>{" "}
            {TT("success.folder", "საქაღალდე.", "folder.")}
          </P>
          <P>
            {TT(
              "success.resendOrSupport",
              "თუ ბმული არ მუშაობს, სცადე ხელახალი გაგზავნა პროფილიდან ან დაგვიკავშირდი მხარდაჭერას.",
              "If the link doesn’t work, try resending it from your profile or contact support."
            )}
          </P>
        </div>

        <Actions>
          <Primary to="/">{TT("success.home", "მთავარი", "Home")}</Primary>
          <Button to="/">{TT("success.continue", "გაგრძელება", "Continue")}</Button>
        </Actions>
      </Card>
    </Page>
  );
}
